'use client';

import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
  type ReactNode,
} from 'react';
import { createBrowserClient } from '@supabase/ssr';
import type { User, Session, SupabaseClient } from '@supabase/supabase-js';
import type { Database } from '@/types/supabase';

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------

export interface AuthUser {
  id: string;
  email: string | null;
  fullName: string | null;
  firstName: string | null;
  lastName: string | null;
  imageUrl: string | null;
  role: 'user' | 'vendor' | 'admin';
  emailVerified: boolean;
  // Clerk compatibility fields
  unsafeMetadata: { role?: string };
  publicMetadata: Record<string, unknown>;
  primaryEmailAddress: { emailAddress: string } | null;
}

interface AuthState {
  user: AuthUser | null;
  isLoaded: boolean;
  isSignedIn: boolean;
}

interface AuthContextValue extends AuthState {
  signOut: () => Promise<void>;
  getToken: (options?: { template?: string }) => Promise<string | null>;
  sessionId: string | null;
  userId: string | null;
  supabase: SupabaseClient<Database>;
}

// -----------------------------------------------------------------------------
// Context
// -----------------------------------------------------------------------------

const AuthContext = createContext<AuthContextValue | null>(null);

// -----------------------------------------------------------------------------
// Provider
// -----------------------------------------------------------------------------

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    isLoaded: false,
    isSignedIn: false,
  });
  const [session, setSession] = useState<Session | null>(null);

  // Create Supabase client once
  const supabase = useMemo(() => {
    const url = process.env.NEXT_PUBLIC_SUPABASE_URL;
    const anonKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY;
    if (!url || !anonKey) {
      throw new Error('Missing Supabase environment variables');
    }
    return createBrowserClient<Database>(url, anonKey);
  }, []);

  // Fetch user profile from profiles table
  const fetchUserProfile = useCallback(
    async (supabaseUser: User): Promise<AuthUser> => {
      const { data: profile } = await supabase
        .from('profiles')
        .select('full_name, role, profile_image_url, email_verified')
        .eq('id', supabaseUser.id)
        .maybeSingle();

      const nameParts = (profile?.full_name || '').split(' ');
      const role = (profile?.role as 'user' | 'vendor' | 'admin') || 'user';

      return {
        id: supabaseUser.id,
        email: supabaseUser.email ?? null,
        fullName: profile?.full_name ?? null,
        firstName: nameParts[0] || null,
        lastName: nameParts.slice(1).join(' ') || null,
        imageUrl: profile?.profile_image_url ?? null,
        role,
        emailVerified: profile?.email_verified ?? supabaseUser.email_confirmed_at !== null,
        // Clerk compatibility
        unsafeMetadata: { role },
        publicMetadata: {},
        primaryEmailAddress: supabaseUser.email
          ? { emailAddress: supabaseUser.email }
          : null,
      };
    },
    [supabase]
  );

  // Initialize auth state
  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session: initialSession } }) => {
      console.log('[Auth] Initial session:', initialSession ? 'exists' : 'none', initialSession?.user?.email);
      setSession(initialSession);
      if (initialSession?.user) {
        // Set signed in immediately (fast) - profile loads after
        setState((prev) => ({ ...prev, isLoaded: true, isSignedIn: true }));
        fetchUserProfile(initialSession.user).then((user) => {
          console.log('[Auth] User profile loaded:', user.email, 'emailVerified:', user.emailVerified);
          setState({ user, isLoaded: true, isSignedIn: true });
        });
      } else {
        setState({ user: null, isLoaded: true, isSignedIn: false });
      }
    });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, newSession) => {
      setSession(newSession);

      if (event === 'SIGNED_OUT' || !newSession?.user) {
        setState({ user: null, isLoaded: true, isSignedIn: false });
        return;
      }

      if (newSession?.user) {
        const user = await fetchUserProfile(newSession.user);
        setState({ user, isLoaded: true, isSignedIn: true });
      }
    });

    return () => subscription.unsubscribe();
  }, [supabase, fetchUserProfile]);

  // Sign out
  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
  }, [supabase]);

  // Get access token (options param is for Clerk API compatibility, ignored here)
  const getToken = useCallback(async (_options?: { template?: string }) => {
    const { data } = await supabase.auth.getSession();
    return data.session?.access_token ?? null;
  }, [supabase]);

  const value: AuthContextValue = {
    ...state,
    signOut,
    getToken,
    sessionId: session?.access_token ?? null,
    userId: state.user?.id ?? null,
    supabase,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// -----------------------------------------------------------------------------
// Hooks
// -----------------------------------------------------------------------------

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return {
    userId: ctx.userId,
    sessionId: ctx.sessionId,
    isLoaded: ctx.isLoaded,
    isSignedIn: ctx.isSignedIn,
    signOut: ctx.signOut,
    getToken: ctx.getToken,
  };
}

export function useUser() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useUser must be used within AuthProvider');
  }
  return {
    user: ctx.user,
    isLoaded: ctx.isLoaded,
    isSignedIn: ctx.isSignedIn,
  };
}

export function useSupabase() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useSupabase must be used within AuthProvider');
  }
  return ctx.supabase;
}
